import { Autocomplete as BaseAutocomplete } from "@base-ui/react/autocomplete";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { useQuery } from "@tanstack/react-query";
import { useId, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { describeError, isApiError } from "@/api/client";
import { useDebouncedValue } from "@/lib/hooks";
import { Icon, type IconName } from "../Icon/Icon";
import { Kbd } from "../Kbd/Kbd";
import { Spinner } from "../Spinner/Spinner";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import styles from "./CommandPalette.module.css";

export interface PaletteItem {
  id: string;
  label: string;
  /** Secondary text: a section ("Stock"), a lot code, a CAS number. */
  detail?: string;
  icon?: IconName;
  /** Extra words that should find this item ("lots", "receive", "stock"). */
  keywords?: readonly string[];
  /** App path to open. */
  to?: string;
  /** Or an action to run. */
  onSelect?: () => void;
}

export interface PaletteGroup {
  id: string;
  label: string;
  items: PaletteItem[];
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Always-available commands (every page), filtered locally. */
  commands: readonly PaletteItem[];
  /** Server search for the typed text; groups are shown above the commands. */
  search?: (query: string, signal: AbortSignal) => Promise<PaletteGroup[]>;
  searchMinLength?: number;
  placeholder?: string;
}

interface ListGroup {
  value: string;
  items: PaletteItem[];
}

/** All words of the query must appear in the label, detail or keywords; label prefixes rank first. */
export function filterCommands(commands: readonly PaletteItem[], query: string): PaletteItem[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [...commands];
  return commands
    .filter((command) => {
      const haystack = [command.label, command.detail ?? "", ...(command.keywords ?? [])].join(" ").toLowerCase();
      return words.every((word) => haystack.includes(word));
    })
    .sort(
      (a, b) =>
        Number(b.label.toLowerCase().startsWith(words[0] ?? "")) -
        Number(a.label.toLowerCase().startsWith(words[0] ?? "")),
    );
}

/**
 * Cmd/Ctrl+K: jump to any page or search records. No open/close animation (used many times a day).
 * Search failures, including a backend without /api/search yet, leave navigation working.
 */
export function CommandPalette({
  open,
  onOpenChange,
  commands,
  search,
  searchMinLength = 2,
  placeholder = "Search materials, lots, formulas, or jump to a page…",
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const query = useDebouncedValue(inputValue.trim(), 150);
  const hintId = useId();
  const searchEnabled = open && search !== undefined && query.length >= searchMinLength;

  const results = useQuery({
    queryKey: ["search", "palette", query],
    queryFn: ({ signal }) => (search ? search(query, signal) : Promise.resolve([])),
    enabled: searchEnabled,
    retry: false,
    staleTime: 30_000,
  });

  const groups = useMemo<ListGroup[]>(() => {
    const list: ListGroup[] = [];
    if (searchEnabled && results.data) {
      for (const group of results.data) {
        if (group.items.length) list.push({ value: group.label, items: group.items });
      }
    }
    const matchingCommands = filterCommands(commands, inputValue.trim());
    if (matchingCommands.length) list.push({ value: inputValue.trim() ? "Pages" : "Go to", items: matchingCommands });
    return list;
  }, [searchEnabled, results.data, commands, inputValue]);

  function changeOpen(next: boolean) {
    if (!next) setInputValue("");
    onOpenChange(next);
  }

  function activate(item: PaletteItem) {
    changeOpen(false);
    if (item.onSelect) item.onSelect();
    else if (item.to) void navigate(item.to);
  }

  let status: ReactNode = null;
  if (searchEnabled && results.isFetching) {
    status = (
      <>
        <Spinner size="sm" label={null} />
        Searching…
      </>
    );
  } else if (searchEnabled && results.isError) {
    status =
      isApiError(results.error, 404) || isApiError(results.error, 501)
        ? "Record search isn't available yet; pages are still listed."
        : `Search failed: ${describeError(results.error)}`;
  }

  return (
    <BaseDialog.Root open={open} onOpenChange={changeOpen}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={styles.backdrop} />
        <BaseDialog.Viewport className={styles.viewport}>
          <BaseDialog.Popup className={styles.popup} aria-label="Command palette">
            <BaseAutocomplete.Root
              open
              inline
              items={groups}
              filter={null}
              value={inputValue}
              onValueChange={(next) => setInputValue(next)}
              itemToStringValue={(item: PaletteItem) => item.label}
              autoHighlight="always"
              keepHighlight
            >
              <BaseAutocomplete.InputGroup className={styles.inputGroup}>
                <Icon name="search" size="md" className={styles.inputIcon} />
                <BaseAutocomplete.Input
                  className={styles.input}
                  aria-label="Search or jump to"
                  aria-describedby={hintId}
                  placeholder={placeholder}
                />
                <Kbd keys={["escape"]} className={styles.escHint} />
              </BaseAutocomplete.InputGroup>
              <BaseDialog.Close className={styles.hiddenClose}>Close command palette</BaseDialog.Close>
              <div className={styles.listArea}>
                <BaseAutocomplete.Empty className={styles.empty}>
                  No pages or records match “{inputValue.trim()}”.
                </BaseAutocomplete.Empty>
                <BaseAutocomplete.List className={styles.list}>
                  {(group: ListGroup) => (
                    <BaseAutocomplete.Group key={group.value} items={group.items} className={styles.group}>
                      <BaseAutocomplete.GroupLabel className={styles.groupLabel}>
                        {group.value}
                      </BaseAutocomplete.GroupLabel>
                      <BaseAutocomplete.Collection>
                        {(item: PaletteItem) => (
                          <BaseAutocomplete.Item
                            key={item.id}
                            value={item}
                            className={styles.item}
                            onClick={() => activate(item)}
                          >
                            <span className={styles.itemIcon}>{item.icon ? <Icon name={item.icon} /> : null}</span>
                            <span className={styles.itemLabel}>{item.label}</span>
                            {item.detail ? <span className={styles.itemDetail}>{item.detail}</span> : null}
                          </BaseAutocomplete.Item>
                        )}
                      </BaseAutocomplete.Collection>
                    </BaseAutocomplete.Group>
                  )}
                </BaseAutocomplete.List>
              </div>
              <div className={styles.footer}>
                <BaseAutocomplete.Status className={styles.status}>{status}</BaseAutocomplete.Status>
                <span className={styles.hints} aria-hidden="true">
                  <Kbd keys={["↑", "↓"]} /> move <Kbd keys={["enter"]} /> open
                </span>
                <VisuallyHidden id={hintId}>
                  Use the arrow keys to move through results and Enter to open one.
                </VisuallyHidden>
              </div>
            </BaseAutocomplete.Root>
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
